import { wait } from '@change-corgi/core/async';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import type { PersonalizedTasksStatus } from '@change-corgi/middle-end-gql-schema';

import { START_STRONG_FEATURE } from '#src/app/shared/constants';
import type { StartStrongTreatOutcome } from '#src/app/shared/utils/startStrong';

import { fetchPersonalizedTasksGenStatus, fetchStartStrongAfterSapAssignment } from './api/startStrongEnrollment';
import { TimeoutError, withTimeout } from './shared/withTimeout';

// Bounded because the redirect awaits petition_service's decision; same budget as
// claimSocialStarterPetitionAngles. Past it, the starter falls back to the standard flow.
const GEN_STATUS_POLL_BUDGET_MS = 8000;
const GEN_STATUS_POLL_INTERVAL_MS = 750;
const ASSIGNMENT_FETCH_TIMEOUT_MS = 3000;

type DecidedGenStatus = Exclude<PersonalizedTasksStatus, 'PENDING'>;

/**
 * Polls until the generation status leaves `PENDING` — petition_service's publish consumer treats
 * eligible starters before it moves the status on — or `deadline` passes (`status: null`). A failed
 * or unreadable poll counts as "not decided yet"; the last error is returned for reporting.
 */
async function pollDecidedGenStatus({
	petitionId,
	utilityContext,
	deadline,
	lastError,
}: {
	petitionId: string;
	utilityContext: UtilityContext;
	deadline: number;
	lastError?: unknown;
}): Promise<Readonly<{ status: DecidedGenStatus | null; lastError: unknown }>> {
	const remainingMs = deadline - Date.now();
	if (remainingMs <= 0) return { status: null, lastError };

	let error: unknown;
	try {
		const status = await withTimeout(
			fetchPersonalizedTasksGenStatus({ petitionId, utilityContext }),
			remainingMs,
			`Polling personalized tasks generation status timed out after ${Math.round(remainingMs)}ms`,
		);
		if (status !== null && status !== 'PENDING') return { status, lastError: undefined };
	} catch (pollError) {
		error = pollError;
	}

	if (deadline - Date.now() <= GEN_STATUS_POLL_INTERVAL_MS) return { status: null, lastError: error };
	await wait(GEN_STATUS_POLL_INTERVAL_MS);
	return pollDecidedGenStatus({ petitionId, utilityContext, deadline, lastError: error });
}

/**
 * Reports a failed read (`undefined` means nothing failed) unless it timed out. Timeouts are
 * expected under load, handled by falling back to the standard flow, and already counted by the
 * `timeout` redirect outcome and `trackTreatExperimentTimeout`.
 */
function reportUnexpectedError({
	utilityContext,
	error,
	operation,
	petitionId,
}: {
	utilityContext: UtilityContext;
	error: unknown;
	operation: string;
	petitionId: string;
}): void {
	if (error === undefined || error instanceof TimeoutError) return;
	void utilityContext.errorReporter.report(
		{
			error: error instanceof Error ? error : new Error(`${operation} failed`, { cause: error }),
			context: { feature: START_STRONG_FEATURE, 'sd.operation': operation, 'petition.id': petitionId },
		},
		{ severity: 'warning' },
	);
}

async function readAssignmentOutcome({
	experimentName,
	petitionId,
	utilityContext,
}: {
	experimentName: string;
	petitionId: string;
	utilityContext: UtilityContext;
}): Promise<
	Readonly<{
		outcome: Extract<StartStrongTreatOutcome, 'treated' | 'control' | 'not_treated' | 'timeout' | 'error'>;
		variation?: string;
	}>
> {
	try {
		const { treated, variation } = await withTimeout(
			fetchStartStrongAfterSapAssignment(utilityContext),
			ASSIGNMENT_FETCH_TIMEOUT_MS,
			`Reading ${experimentName} timed out after ${ASSIGNMENT_FETCH_TIMEOUT_MS}ms`,
		);
		if (!treated) return { outcome: 'not_treated', variation };
		return { outcome: variation === 'variant_a' || variation === 'variant_b' ? 'treated' : 'control', variation };
	} catch (error) {
		reportUnexpectedError({ utilityContext, error, operation: 'SAPStartStrongAfterSapAssignment', petitionId });
		return { outcome: error instanceof TimeoutError ? 'timeout' : 'error' };
	}
}

/**
 * Resolves whether this publish redirects to start-strong, for `resolvePostPublishDestination`.
 *
 * petition_service owns who is treated into starter_experience_start_strong_after_sap, so this
 * never treats: it waits for the publish consumer's decision (the generation status leaving
 * `PENDING`) and reads the assignment back untreated. `SKIPPED` means ineligible, and `FAILED` (or
 * any status this doesn't know) leaves no plan to show. On timeout the consumer may still treat the
 * starter afterwards, hence `trackTreatExperimentTimeout`, so analysts can filter those sessions out.
 *
 * A pre-publish `control` waits and reads back like the variants: the middle-end's experiments read
 * is what stores the consumer's treat on the session's `experiment_variations`, which
 * activate_petition carries. The read-back, not the pre-publish variation, decides the redirect.
 * Ambassador petitions and a `fallback` (the experiment is off) skip the wait; a QA/dev `override`
 * is honored as is.
 */
export async function resolveIsStartStrongAfterSapEnabled({
	experimentName,
	variation,
	override,
	fallback,
	isAmbassadorPetition,
	petitionId,
	utilityContext,
	trackTreatExperimentTimeout,
}: {
	experimentName: string;
	variation: string;
	override: true | undefined;
	fallback: true | undefined;
	isAmbassadorPetition: boolean;
	petitionId: string;
	utilityContext: UtilityContext;
	trackTreatExperimentTimeout: (args: { experimentName: string; variation: string; petitionId: string }) => void;
}): Promise<StartStrongAfterSapResolution> {
	const startedAt = performance.now();
	const resolved = (
		enabled: boolean,
		treatOutcome: StartStrongTreatOutcome,
		resolvedVariation: string = variation,
	): StartStrongAfterSapResolution => ({
		enabled,
		treatOutcome,
		variation: resolvedVariation,
		resolveLatencyMs: Math.round(performance.now() - startedAt),
	});
	const timedOut = () => {
		trackTreatExperimentTimeout({ experimentName, variation, petitionId });
		return resolved(false, 'timeout');
	};

	if (isAmbassadorPetition || fallback === true) return resolved(false, 'ineligible');
	if (override === true) return resolved(variation !== 'control', 'override');

	const { status, lastError } = await pollDecidedGenStatus({
		petitionId,
		utilityContext,
		deadline: Date.now() + GEN_STATUS_POLL_BUDGET_MS,
	});
	if (status === null) {
		reportUnexpectedError({
			utilityContext,
			error: lastError,
			operation: 'SAPPersonalizedTasksGenStatus',
			petitionId,
		});
		return timedOut();
	}
	if (status === 'SKIPPED') return resolved(false, 'skipped');
	if (status !== 'IN_PROGRESS' && status !== 'COMPLETED') return resolved(false, 'generation_failed');

	const { outcome, variation: readVariation } = await readAssignmentOutcome({
		experimentName,
		petitionId,
		utilityContext,
	});
	if (outcome === 'timeout') return timedOut();
	return resolved(outcome === 'treated', outcome, readVariation);
}

type StartStrongAfterSapResolution = Readonly<{
	enabled: boolean;
	treatOutcome: StartStrongTreatOutcome;
	variation: string;
	resolveLatencyMs: number;
}>;
